import { AnyExtension, Extension } from "@tiptap/core";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import { compact } from "lodash";
import { useEffect, useMemo, useRef } from "react";

import { classNames } from "@helpers/css";

const TextOnlyWysiwyg = ({
  deps = [],
  value,
  editable = false,
  autofocus = false,
  placeholder = "",
  className = "",
  extraExtensions,
  onChangeValue,
  onEnterPressed,
  onEditorReady,
}: {
  deps?: any[];
  value?: string | null;
  autofocus?: boolean;
  editable?: boolean;
  placeholder?: string;
  className?: string;
  extraExtensions?: AnyExtension[];
  onChangeValue?: (value: string) => void;
  onEnterPressed?: () => void;
  onEditorReady?: (editor: Editor) => void;
}) => {
  const content = !value // if falsy, return null
    ? null
    : value; // or we just return the string value.

  const onEnterPressedRef = useRef(onEnterPressed);
  onEnterPressedRef.current = onEnterPressed;

  const enterSubmitExtension = useMemo(
    () =>
      Extension.create({
        name: "textOnlyEnterSubmit",
        addKeyboardShortcuts() {
          return {
            Enter: () => {
              onEnterPressedRef.current?.();
              return true;
            },
          };
        },
      }),
    [],
  );

  const editor = useEditor(
    {
      injectNonce: window.TF?.cspNonce,
      editable,
      autofocus,
      extensions: compact([
        Text,
        Paragraph,
        HardBreak.configure({ keepMarks: false }),
        Document,
        Placeholder.configure({
          placeholder: placeholder,
          showOnlyCurrent: true,
        }),
        ...(extraExtensions ?? []),
        enterSubmitExtension,
      ]),
      editorProps: {
        attributes: {
          class: classNames(className, "no-placeholder-on-every-line"),
        },
      },
      content,
    },
    [...deps, extraExtensions],
  );

  useEffect(() => {
    const handleUpdateValue = ({ editor }: any) => {
      if (onChangeValue) onChangeValue(editor.getText());
    };
    editor?.on("update", handleUpdateValue);
    return function cleanup() {
      editor?.off("update", handleUpdateValue);
    };
  }, [editor, onChangeValue]);

  useEffect(() => {
    if (onEditorReady && editor) onEditorReady(editor);
  }, [editor, onEditorReady]);

  return (
    <>
      <EditorContent editor={editor} className="flex-1 min-w-0" />
    </>
  );
};

export default TextOnlyWysiwyg;
